import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  FolderDown,
  History,
  Keyboard,
  LayoutDashboard,
  Settings,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { USER_DAILY_LIMIT } from '@/constants/limits';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useStats } from '@/hooks/useStats';
import { useUiStore } from '@/store/uiStore';
import { cn } from '@/utils/cn';
import type { TranslationKey } from '@/constants/i18n';

export interface NavItem {
  to: string;
  key: TranslationKey;
  icon: LucideIcon;
  end?: boolean;
  adminOnly?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { to: ROUTES.dashboard, key: 'dash.overview', icon: LayoutDashboard, end: true },
  { to: ROUTES.history, key: 'dash.history', icon: History },
  { to: ROUTES.files, key: 'dash.files', icon: FolderDown },
  { to: ROUTES.settings, key: 'dash.settings', icon: Settings },
  { to: ROUTES.admin, key: 'dash.admin', icon: BarChart3, adminOnly: true },
];

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { isAdmin } = useAuth();
  const { t } = useI18n();
  const stats = useStats();
  const setShortcutsOpen = useUiStore((s) => s.setShortcutsOpen);

  const quotaPct = stats.dailyLimit > 0 ? (stats.downloadsToday / stats.dailyLimit) * 100 : 0;

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-6 pt-5">
        <Logo />
      </div>

      <nav className="flex-1 space-y-1 px-3" aria-label="Dashboard">
        {NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-medium transition-colors focus-ring',
                isActive
                  ? 'bg-primary-500/15 text-primary-300'
                  : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100'
              )
            }
          >
            <item.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" aria-hidden />
            {t(item.key)}
          </NavLink>
        ))}
      </nav>

      <div className="space-y-4 p-4">
        <button
          onClick={() => setShortcutsOpen(true)}
          className="flex w-full items-center gap-2 rounded-2xl px-4 py-2 text-xs text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-300 focus-ring"
        >
          <Keyboard className="h-4 w-4" aria-hidden />
          Keyboard shortcuts
          <kbd className="ml-auto rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px]">?</kbd>
        </button>

        <div className="glass p-4">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-zinc-300">{t('stats.today')}</span>
            <span className="text-zinc-500">
              {stats.downloadsToday}/{stats.dailyLimit}
            </span>
          </div>
          <ProgressBar value={quotaPct} className="mt-2.5" />
          {stats.isGuest && (
            <p className="mt-2.5 text-[11px] leading-snug text-zinc-500">
              Sign in free to unlock {USER_DAILY_LIMIT} downloads/day.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function Sidebar() {
  const mobileOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileOpen = useUiStore((s) => s.setMobileSidebarOpen);

  return (
    <>
      {/* Desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-white/5 bg-surface-raised/50 backdrop-blur-xl lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="absolute inset-y-0 left-0 w-72 border-r border-white/10 bg-surface-raised"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
            >
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute right-3 top-4 rounded-lg p-1.5 text-zinc-500 hover:bg-white/5 hover:text-zinc-200 focus-ring"
                aria-label="Close sidebar"
              >
                <X className="h-5 w-5" />
              </button>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
